import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../LoadingSpinner';
import NotificationCard from '../notifications/NotificationCard';
import useNotifications from '../../hooks/useNotifications';
import { useProfileRole } from '../../hooks/useProfileRole';

const NOTIFICATIONS_COUNT = 2;
const ADMIN_NOTIFICATIONS_COUNT = 4;
const DASHBOARD_NOTIFICATIONS_MARK_AS_READ_DELAY = 5000;

const NotificationsSection: React.FC = () => {
  const { t } = useTranslation(['notifications', 'dashboard']);

  const { isAdmin } = useProfileRole();

  const { unreadCount, loading, notifications } = useNotifications({
    initialCount: isAdmin ? ADMIN_NOTIFICATIONS_COUNT : NOTIFICATIONS_COUNT,
    markAsReadDelay: DASHBOARD_NOTIFICATIONS_MARK_AS_READ_DELAY,
  });

  const notificationsToShow = notifications.slice(
    0,
    isAdmin ? ADMIN_NOTIFICATIONS_COUNT : NOTIFICATIONS_COUNT
  );
  return (
    <section>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center">
          <h1 className="relative min-w-0 text-base font-bold text-secondary">
            <span className="truncate text-xl">{t('dashboard:notifications.title')}</span>

            {(unreadCount ?? 0) > 0 && (
              <span className="absolute -top-2 -right-3 flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-xs font-normal text-white">
                {unreadCount}
              </span>
            )}
          </h1>
        </div>

        <Button
          variant="transparent-with-border"
          href={isAdmin ? '/admin/notifications' : '/notifications'}
          className="ml-2 hidden flex-shrink-0 whitespace-nowrap text-xs md:flex"
        >
          {t('dashboard:notifications.view_all')}
        </Button>
      </div>

      <div className="flex flex-col">
        {loading && notificationsToShow.length === 0 && (
          <div className="flex h-10 items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        {notificationsToShow.length === 0 && !loading && (
          <div className="mb-4 text-sm text-gray-500">{t('noNotifications')}</div>
        )}

        {notificationsToShow.map(notif => (
          <NotificationCard
            key={notif.id}
            notification={notif}
            outerClassName="rounded-none border-x-0 border-b border-t-0 border-gray-300 p-0 py-2 text-xs shadow-none last:border-b-0 md:p-0 md:py-2"
          />
        ))}
      </div>

      <div className="mt-4 md:hidden">
        <Link
          to="/notifications"
          className="block w-full rounded-full border border-primary bg-white py-2 text-center font-bold text-secondary shadow-sm"
        >
          {t('dashboard:notifications.view_all')}
        </Link>
      </div>
    </section>
  );
};

export default NotificationsSection;
